import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import '../styles/Login.css';

const API = process.env.REACT_APP_API_URL;
const STATIC_BASE = process.env.REACT_APP_API_URL?.replace(/\/api\/v\d+$/, '') || '';
const authHeader = () => ({
  Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
  'Content-Type': 'application/json',
  accept: 'application/json',
});

const ENCOURAGEMENTS = [
  'Con đang làm tốt lắm! 🌟',
  'Cố lên, sắp xong rồi! 💪',
  'Tuyệt vời, tiếp tục nhé! 🚀',
  'Con giỏi lắm! ✨',
  'Gần đến đích rồi! 🎯',
];

const PlacementTest = () => {
  const navigate = useNavigate();
  const [quizzes, setQuizzes] = useState([]);
  const [current, setCurrent] = useState(0);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchQuizzes = async () => {
      try {
        const res = await fetch(`${API}/client/placement-test/generate`, {
          headers: authHeader(),
        });
        if (!res.ok) throw new Error('Không thể tải bài test');
        const data = await res.json();
        setQuizzes(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchQuizzes();
  }, []);

  const handleAnswer = (optionKey) => {
    const quiz = quizzes[current];
    setAnswers((prev) => ({ ...prev, [quiz.id]: optionKey }));
  };

  const handleNext = () => {
    if (current < quizzes.length - 1) {
      setCurrent((c) => c + 1);
    } else {
      handleSubmit();
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const payload = quizzes.map((q) => ({
        quiz_id: q.id,
        user_answer: answers[q.id] || '',
      }));
      const res = await fetch(`${API}/client/placement-test/submit`, {
        method: 'POST',
        headers: authHeader(),
        body: JSON.stringify({ answers: payload }),
      });
      if (!res.ok) throw new Error('Lỗi khi nộp bài');
      const result = await res.json();
      localStorage.setItem('hasCompletedPlacement', 'true');
      localStorage.setItem('placementResult', JSON.stringify(result));
      navigate('/placement-result');
    } catch (err) {
      setError(err.message);
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="ob-sky-voyager min-h-screen flex items-center justify-center">
        <div className="text-center">
          <span className="material-symbols-outlined text-6xl text-green-600 lf-spin">progress_activity</span>
          <p className="mt-4 text-hp-on-surface-variant font-semibold">Đang chuẩn bị bài test...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="ob-sky-voyager min-h-screen flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl p-8 text-center max-w-md">
          <span className="text-4xl">😔</span>
          <p className="mt-4 text-red-600 font-semibold">{error}</p>
          <button onClick={() => navigate('/placement-test-intro')} className="mt-6 hp-button-gradient text-white font-bold py-3 px-8 rounded-lg">
            Quay lại
          </button>
        </div>
      </div>
    );
  }

  if (!quizzes.length) return null;

  const quiz = quizzes[current];
  const progress = Math.round(((current + 1) / quizzes.length) * 100);
  const selectedAnswer = answers[quiz.id];
  const encouragement = ENCOURAGEMENTS[current % ENCOURAGEMENTS.length];

  // Parse question content
  const questionText = quiz.question?.question_text || quiz.question?.text || quiz.question?.stem || '';
  const contextText = quiz.context?.context_text || quiz.context?.text || quiz.context?.passage || null;
  const contextImageUrl = quiz.context?.context_image_url || null;
  const options = quiz.options || [];
  const correctAnswer = quiz.answer_feedback?.correct_answer_text || null;

  // Detect fill-in-the-blank quiz (no options, has blank/label)
  const isFillBlank = options.length === 0 && (quiz.question?.blank || quiz.question?.label);
  const blankLabel = quiz.question?.label || '';
  const textBefore = quiz.question?.text_before_blank || '';
  const textAfter = quiz.question?.text_after_blank || '';

  return (
    <div className="ob-sky-voyager font-body min-h-screen flex flex-col items-center justify-center p-4 relative overflow-x-hidden">
      <div className="fixed inset-0 ob-cloud-pattern pointer-events-none" />
      <div className="fixed inset-0 ob-star-pattern opacity-30 pointer-events-none" />

      <main className="w-full max-w-[640px] my-8 z-10">
        {/* Progress bar */}
        <div className="mb-6">
          <div className="flex justify-between items-center mb-2">
            <span className="text-sm font-semibold text-hp-on-surface-variant">
              Câu {current + 1}/{quizzes.length}
            </span>
            <span className="text-sm font-semibold text-green-600">{progress}%</span>
          </div>
          <div className="w-full h-3 bg-hp-surface-container rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-hupath-scout to-hupath-leaf rounded-full transition-all duration-500"
              style={{ width: `${progress}%` }}
            />
          </div>
          <p className="text-xs text-hp-on-surface-variant mt-1 text-right italic">{encouragement}</p>
        </div>

        {/* Quiz card */}
        <div className="bg-white rounded-2xl shadow-[0px_12px_32px_rgba(94,46,26,0.08)] p-8">
          <p className="text-xs font-semibold text-green-600 uppercase tracking-wider mb-4">
            {quiz.exercise_type?.replace(/_/g, ' ')}
          </p>

          {/* Context image */}
          {contextImageUrl && (
            <img
              src={`${STATIC_BASE}${contextImageUrl}`}
              alt="Context"
              className="w-full rounded-xl mb-4 object-cover"
            />
          )}

          {/* Context text */}
          {contextText && (
            <div className="bg-blue-50 rounded-xl p-4 mb-6 text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">
              {contextText}
            </div>
          )}

          {isFillBlank ? (
            /* ── Fill-in-the-blank ── */
            <>
              {/* Label */}
              {blankLabel && (
                <p className="text-sm font-semibold text-gray-500 mb-2">{blankLabel}</p>
              )}

              {/* Blank sentence */}
              <div className="bg-gray-50 rounded-xl p-4 mb-4 text-lg font-semibold text-hp-on-surface flex items-center flex-wrap gap-1">
                {textBefore && <span>{textBefore}</span>}
                <input
                  type="text"
                  value={selectedAnswer || ''}
                  onChange={(e) => setAnswers((prev) => ({ ...prev, [quiz.id]: e.target.value }))}
                  placeholder="điền đáp án"
                  className="border-b-2 border-green-400 bg-transparent outline-none text-center font-bold text-green-700 min-w-[120px] px-2 py-1"
                  disabled={!!selectedAnswer && selectedAnswer === selectedAnswer}
                />
                {textAfter && <span>{textAfter}</span>}
              </div>

              {/* Show correct answer after submit */}
              {selectedAnswer && correctAnswer && (
                <div className={`rounded-xl p-3 mb-4 text-sm font-semibold ${
                  selectedAnswer.trim().toLowerCase() === correctAnswer.trim().toLowerCase()
                    ? 'bg-green-50 text-green-700 border border-green-300'
                    : 'bg-red-50 text-red-700 border border-red-300'
                }`}>
                  {selectedAnswer.trim().toLowerCase() === correctAnswer.trim().toLowerCase()
                    ? '✓ Chính xác!'
                    : `✗ Đáp án đúng: ${correctAnswer}`}
                </div>
              )}

              {/* Confirm button for fill-blank */}
              {!selectedAnswer?.trim() ? null : null}
            </>
          ) : (
            /* ── Multiple choice ── */
            <>
              {/* Question */}
              <h2 className="font-headline text-xl font-bold text-hp-on-surface mb-6 leading-snug">
                {questionText}
              </h2>

              {/* Options */}
              <div className="space-y-3 mb-8">
                {options.map((opt, idx) => {
                  const key = opt.id || opt.key || opt.label || String.fromCharCode(65 + idx);
                  const text = opt.text || opt.value || opt;
                  const isSelected = selectedAnswer === key;

                  let borderColor = 'border-gray-200 bg-gray-50 hover:border-green-300 hover:bg-green-50/50 text-hp-on-surface';
                  if (isSelected) borderColor = 'border-green-500 bg-green-50 text-green-800';

                  return (
                    <button
                      key={key}
                      onClick={() => !selectedAnswer && handleAnswer(key)}
                      className={`w-full text-left p-4 rounded-xl border-2 font-semibold transition-all duration-200 ${borderColor}`}
                    >
                      <span className={`inline-flex items-center justify-center w-7 h-7 rounded-full mr-3 text-sm font-bold
                        ${isSelected ? 'bg-green-500 text-white' : 'bg-gray-200 text-gray-600'}`}>
                        {key}
                      </span>
                      {text}
                    </button>
                  );
                })}
              </div>
            </>
          )}

          {/* Tip */}
          <p className="text-xs text-hp-on-surface-variant italic mb-6">
            💡 Không sao nếu con chưa biết, hãy chọn đáp án con nghĩ đúng nhất!
          </p>

          {/* Next button */}
          <button
            onClick={handleNext}
            disabled={submitting}
            className="w-full hp-button-gradient text-white font-headline font-bold text-lg py-4 rounded-xl shadow-lg disabled:opacity-40 disabled:cursor-not-allowed hover:scale-[1.02] active:scale-[0.98] transition-all duration-300 flex items-center justify-center gap-2"
          >
            {submitting ? (
              <><span className="material-symbols-outlined lf-spin">progress_activity</span> Đang nộp bài...</>
            ) : current < quizzes.length - 1 ? (
              <>Tiếp theo <span className="material-symbols-outlined">arrow_forward</span></>
            ) : (
              <>Nộp bài <span className="material-symbols-outlined">check_circle</span></>
            )}
          </button>
        </div>
      </main>

      <div className="fixed bottom-[-100px] left-1/2 -translate-x-1/2 w-[120%] h-[300px] bg-white rounded-[50%] blur-3xl opacity-60 z-0" />
    </div>
  );
};

export default PlacementTest;
